import {
  coordEq,
  getTile,
  previewCapture,
  previewJoin,
  unitAt,
  unitDef,
} from "@dailywars/core/engine";
import type { Coord, GameState } from "@dailywars/core/engine";

import { getBoardCursorMode } from "#/game/selection/cursor";
import { getSelectUnitHoverTarget } from "#/game/selection/selectUnitHover";
import type { SelectionState } from "#/game/selection/types";

export type BoardActionHint =
  | { kind: "none" }
  | { kind: "cancel" }
  | { kind: "move" }
  | { kind: "move-to-join" }
  | { kind: "wait" }
  | { kind: "build" }
  | { kind: "load" }
  | { kind: "unload-here" }
  | { kind: "unload-select" }
  | { kind: "unload-or-wait" }
  | { kind: "attack"; forecast: number }
  | { kind: "capture"; from: number; to: number; full: number }
  | { kind: "join"; fromHp: number; intoHp: number; resultHp: number; refund: number }
  | { kind: "select-unit"; name: string };

type ActiveSelection = Extract<SelectionState, { mode: "unit" | "move-preview" }>;

function isActiveSelection(selection: SelectionState): selection is ActiveSelection {
  return selection.mode === "unit" || selection.mode === "move-preview";
}

function joinHint(state: GameState, selection: SelectionState): BoardActionHint {
  if (selection.mode !== "move-preview") return { kind: "none" };
  const source = state.units.find((u) => u.id === selection.unitId);
  const target = unitAt(state, selection.destination);
  if (!source || !target) return { kind: "none" };
  const { fromHp, intoHp, resultHp, refund } = previewJoin({ source, target });
  return { kind: "join", fromHp, intoHp, resultHp, refund };
}

function captureHint(state: GameState, selection: ActiveSelection): BoardActionHint {
  const unit = state.units.find((u) => u.id === selection.unitId);
  if (!unit) return { kind: "none" };
  const pos = selection.mode === "move-preview" ? selection.destination : unit.pos;
  const { from, to, full } = previewCapture({ tile: getTile(state, pos), unit });
  return { kind: "capture", from, to, full };
}

function selectUnitHint({
  state,
  selection,
  hoveredTile,
  coarsePointer,
}: {
  state: GameState;
  selection: SelectionState;
  hoveredTile: Coord | null;
  coarsePointer: boolean;
}): BoardActionHint | null {
  const unit = getSelectUnitHoverTarget({ state, selection, hoveredTile, coarsePointer });
  if (!unit) return null;
  return { kind: "select-unit", name: unitDef(unit.kind).name };
}

function idleHint({ selection }: { selection: SelectionState }): BoardActionHint {
  if (isActiveSelection(selection)) return { kind: "cancel" };
  return { kind: "none" };
}

function moveHint({
  state,
  selection,
  hoveredTile,
}: {
  state: GameState;
  selection: SelectionState;
  hoveredTile: Coord | null;
}): BoardActionHint {
  if (!isActiveSelection(selection)) {
    return { kind: "move" };
  }
  if (!hoveredTile) return { kind: "move" };
  const hoveredUnit = unitAt(state, hoveredTile);
  if (hoveredUnit && selection.joinOptions.some((option) => option.targetId === hoveredUnit.id)) {
    return { kind: "move-to-join" };
  }
  return { kind: "move" };
}

function attackHint({
  state,
  selection,
  hoveredTile,
}: {
  state: GameState;
  selection: SelectionState;
  hoveredTile: Coord | null;
}): BoardActionHint {
  if (!hoveredTile) return { kind: "none" };
  if (!isActiveSelection(selection)) return { kind: "none" };
  const enemy = unitAt(state, hoveredTile);
  const option = enemy && selection.attackOptions.find((o) => o.targetId === enemy.id);
  return option ? { kind: "attack", forecast: option.forecast } : { kind: "none" };
}

export function getBoardActionHint({
  state,
  selection,
  hoveredTile,
  coarsePointer = false,
}: {
  state: GameState;
  selection: SelectionState;
  hoveredTile: Coord | null;
  coarsePointer?: boolean;
}): BoardActionHint {
  if (selection.mode === "transport-unload") {
    if (!hoveredTile) return { kind: "unload-or-wait" };
    const transport = state.units.find((u) => u.id === selection.unitId);
    if (transport && coordEq(hoveredTile, transport.pos)) return { kind: "unload-or-wait" };
    if (selection.unloadTiles.some((c) => coordEq(c, hoveredTile))) return { kind: "unload-here" };
    return { kind: "cancel" };
  }

  const mode = getBoardCursorMode({ state, selection, hoveredTile, coarsePointer });
  switch (mode) {
    case "select":
      return selectUnitHint({ state, selection, hoveredTile, coarsePointer }) ?? { kind: "none" };
    case "join":
      return joinHint(state, selection);
    case "capture":
      if (!isActiveSelection(selection)) return { kind: "none" };
      return captureHint(state, selection);
    case "attack":
      return attackHint({ state, selection, hoveredTile });
    case "move":
      return moveHint({ state, selection, hoveredTile });
    case "wait":
      return { kind: "wait" };
    case "load":
      return { kind: "load" };
    case "build":
      return { kind: "build" };
    case "download":
      return { kind: "unload-here" };
    case "default":
      return idleHint({ selection });
  }
}
